import { ArrowForward } from "@mui/icons-material"
import StarIcon from "@mui/icons-material/Star"
import React, { ReactNode } from "react"

import { useBooking } from "../../../states/bookingDetail"
import { DefaultImage } from "../lazyImage/DefaultImage"
import { BaseLink } from "src/components/common/base-link"
import { Card } from "src/components/common/card"
import { LazyImage } from "src/components/common/lazyImage"

import { create } from "src/helpers/bem"

import { formatPrice } from "src/helpers/price"

import { useTranslation } from "src/hooks/translation"
import { useLocale } from "src/states/locale"
import { Product } from "src/states/shop/products"

import styles from "./ProductCard.module.scss"

const bem = create(styles, "ProductCard")

export type ProductCardImageProps = {
  src: string
  alt: string
}

export type ProductCardProps = Omit<
  Product,
  "contents" | "productAttributesForm" | "productAttributeDependencies"
> & {
  className?: string
  pricePrefix?: string
}

export const ProductCard: React.FC<ProductCardProps> = ({
  className,
  id,
  image,
  tag,
  favorite,
  name,
  note,
  pricePrefix,
  price,
  unit,
  productCalculation,
}) => {
  const locale = useLocale()
  const { booking } = useBooking()
  const { messages } = useTranslation()

  const renderLink = (children: ReactNode, className?: string) => {
    return (
      <BaseLink
        className={className && bem(className)}
        href={`/${locale}/${messages.pages.shop.overview.products.route}?id=${id}`}
      >
        {children}
      </BaseLink>
    )
  }

  return (
    <Card
      classNames={{
        root: bem(undefined, undefined, className),
        content: bem("wrapper"),
      }}
    >
      {tag ? <span className={bem("header__tag")}>{tag}</span> : null}
      {favorite ? <StarIcon className={bem("header__icon")} /> : null}
      {renderLink(
        <div className={bem("image")}>
          {image?.src ? (
            <div>
              <LazyImage
                alt={image?.alt ?? ""}
                className={bem("image__lazy", "has-link: true")}
                src={image.src}
              />
            </div>
          ) : (
            <DefaultImage />
          )}
        </div>,
      )}
      <div className={bem("content")}>
        {renderLink(
          <h6 className={bem("title", "has-link: true")}>{name}</h6>,
          "title__link",
        )}
        <p className={bem("note")}>{note}</p>
        <div className={bem("footer__border")} />
        <div className={bem("footer")}>
          {price != null ? (
            <p className={bem("price__wrapper")}>
              {pricePrefix}&nbsp;
              <span className={bem("price")}>
                {booking !== undefined ||
                (!booking && productCalculation !== "Charges")
                  ? formatPrice(price, locale)
                  : "-"}
              </span>
              {unit && productCalculation === "Fix" && (
                <span className={bem("unit")}>&nbsp;/&nbsp;{unit}</span>
              )}
            </p>
          ) : (
            <span className={bem("unit")}>
              {messages.pages.shop.overview.noPrice}
            </span>
          )}
          {renderLink(
            <button className={bem("button")}>
              <ArrowForward color="primary" />
            </button>,
          )}
        </div>
      </div>
    </Card>
  )
}
